import React, { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, createSeriesMarkers, ColorType, LineStyle } from "lightweight-charts";
import client from "@/lib/api";

const LIME = "#ccff00", RED = "#f87171", WHITE = "#fafafa";
const EVENT_LABEL = { TAKE_PROFIT_SIGNAL: "TP", CUT_LOSS_SIGNAL: "SL", STOP_UPDATED: "S↺", TARGET_UPDATED: "T↺", CONVICTION_CLOSED: "X", ENTRY: "B", MODEL_TP_EXECUTED: "TP✓", MODEL_STOP_EXECUTED: "SL✓", TARGET_TOUCHED: "t", STOP_TOUCHED: "s" };
const RED_TYPES = ["CUT_LOSS_SIGNAL", "CONVICTION_CLOSED", "MODEL_STOP_EXECUTED", "STOP_TOUCHED"];
const ts = (iso) => Math.floor(new Date(iso).getTime() / 1000);
const fmt = (iso) => new Date(iso).toLocaleString();

// Maps an exact timestamp to the candle bucket that contains it; never invents a candle.
export function bucketFor(t, interval, first, last) {
  const b = Math.floor(t / interval) * interval;
  return b < first || b > last ? null : b;
}

export function ConvictionChart({ conv }) {
  const el = useRef(null);
  const tip = useRef(null);
  const [tf, setTf] = useState("4H");
  const [data, setData] = useState(null);
  const [outside, setOutside] = useState([]);

  useEffect(() => { setData(null); setOutside([]); client.get(`/v2/convictions/${conv.id}/candles`, { params: { tf } }).then((r) => setData(r.data)).catch(() => setData({ available: false, reason: "Market data request failed" })); }, [conv.id, conv.asset_id, conv.rev, tf]);

  useEffect(() => {
    if (!data?.available || !el.current) return;
    const chart = createChart(el.current, {
      autoSize: true,
      localization: { locale: "en-US" },
      layout: { background: { type: ColorType.Solid, color: "#0a0a0a" }, textColor: "#a1a1aa", fontFamily: "inherit", attributionLogo: true },
      grid: { vertLines: { color: "#161616" }, horzLines: { color: "#161616" } },
      rightPriceScale: { borderColor: "#27272a" }, timeScale: { borderColor: "#27272a", timeVisible: tf !== "1D" },
      crosshair: { vertLine: { color: "#52525b" }, horzLine: { color: "#52525b" } },
    });
    const s = chart.addSeries(CandlestickSeries, { upColor: LIME, downColor: "#52525b", wickUpColor: LIME, wickDownColor: "#71717a", borderVisible: false,
      autoscaleInfoProvider: (orig) => {
        const r = orig();
        const lv = conv.locked ? [] : [conv.entry_ref_price, conv.levels?.tp1, conv.levels?.tp2, conv.levels?.tp3, conv.levels?.stop].filter(Boolean).map(Number);
        if (!r || !lv.length) return r;
        return { ...r, priceRange: { minValue: Math.min(r.priceRange.minValue, ...lv), maxValue: Math.max(r.priceRange.maxValue, ...lv) } };
      } });
    const c = data.candles;
    const dec = Math.min(8, Math.max(2, ...[c[c.length - 1].close, conv.entry_ref_price, ...Object.values(conv.levels || {})].filter(Boolean).map((v) => (String(v).split(".")[1] || "").length)));
    s.applyOptions({ priceFormat: { type: "price", precision: dec, minMove: 1 / 10 ** dec } });
    s.setData(c.map((x) => ({ time: x.time, open: +x.open, high: +x.high, low: +x.low, close: +x.close })));
    const line = (p, title, color, style) => p && s.createPriceLine({ price: +p, title, color, lineStyle: style, lineWidth: 1, axisLabelVisible: true });
    if (!conv.locked) {
      line(conv.entry_ref_price, "Entry", WHITE, LineStyle.Solid);
      ["tp1", "tp2", "tp3"].forEach((k) => line(conv.levels?.[k], k.toUpperCase(), LIME, LineStyle.Dashed));
      line(conv.levels?.stop, "Cut loss", RED, LineStyle.Dotted);
    }
    const first = c[0].time, last = c[c.length - 1].time, iv = data.interval_seconds;
    const pts = conv.locked ? [] : [{ type: "ENTRY", effective_at: conv.entry_ref_at, price: conv.entry_ref_price, note: "Entry reference" }, ...(conv.events || []).filter((e) => e.type !== "MARKET_DATA_GAP")];
    const byBucket = {}, out = [];
    pts.forEach((e) => {
      const b = e.effective_at && bucketFor(ts(e.effective_at), iv, first, last);
      if (b === null || b === undefined) { out.push(e); return; }
      (byBucket[b] = byBucket[b] || []).push(e);
    });
    setOutside(out);
    const markers = Object.keys(byBucket).map(Number).sort((a, b) => a - b).flatMap((b) => byBucket[b].map((e) => ({
      time: b, position: RED_TYPES.includes(e.type) ? "aboveBar" : "belowBar",
      shape: e.type === "ENTRY" ? "arrowUp" : RED_TYPES.includes(e.type) ? "arrowDown" : e.model ? "square" : "circle",
      color: RED_TYPES.includes(e.type) ? RED : e.type === "ENTRY" ? WHITE : LIME, text: EVENT_LABEL[e.type] || "•",
    })));
    createSeriesMarkers(s, markers);
    const entryT = conv.entry_ref_at && bucketFor(ts(conv.entry_ref_at), iv, first, last);
    if (entryT && !conv.locked) chart.timeScale().setVisibleRange({ from: Math.max(first, entryT - 60 * iv), to: last });
    else chart.timeScale().fitContent();
    chart.subscribeCrosshairMove((p) => {
      const evs = p.time !== undefined ? byBucket[p.time] : null;
      if (!tip.current) return;
      if (!evs || !p.point) { tip.current.style.display = "none"; return; }
      tip.current.style.display = "block";
      tip.current.style.left = `${Math.min(p.point.x + 12, el.current.clientWidth - 230)}px`;
      tip.current.style.top = `${Math.max(p.point.y - 20, 4)}px`;
      tip.current.innerHTML = evs.map((e) => `<b>${e.type.replace(/_/g, " ")}</b><br/>${e.actor_type === "system" ? "Automatic monitor" : conv.analyst?.name || ""} · ${fmt(e.effective_at)}${e.price ? `<br/>Price ${e.price} ${data.quote}` : ""}${e.exit_pct ? ` · exit ${e.exit_pct}% of original` : ""}${e.delayed ? "<br/><i>Reported later</i>" : ""}${e.simulated ? "<br/><i>Simulated model, automatic</i>" : ""}${e.ambiguous_order ? "<br/><i>Ambiguous candle: stop assumed first</i>" : ""}${e.note ? `<br/>${e.note.replace(/</g, "&lt;").slice(0, 140)}` : ""}`).join("<hr style='border-color:#27272a;margin:6px 0'/>");
    });
    return () => chart.remove();
  }, [data, conv, tf]);

  return (
    <div data-testid="conviction-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex gap-1.5" role="group" aria-label="Timeframe">
          {data?.available !== false && ["1H", "4H", "1D"].map((t) => (
            <button key={t} data-testid={`chart-tf-${t}`} onClick={() => setTf(t)} aria-pressed={tf === t}
              className={`px-3 py-1.5 rounded-full text-xs font-mono-cl ${tf === t ? "bg-white text-black" : "border border-white/10 text-zinc-400 hover:text-white"}`}>{t}</button>
          ))}
        </div>
        {data?.available && (
          <p className="text-[11px] text-zinc-500" data-testid="chart-source">
            {data.provider} · {data.pair} · quote {data.quote} · fetched {fmt(data.fetched_at)}
            {data.stale && <span className="ml-2 text-amber-300">STALE: no recent candles</span>}
            {data.reason && <span className="ml-2 text-amber-300">{data.reason}</span>}
          </p>
        )}
      </div>
      {data === null && <div className="h-[340px] sm:h-[440px] rounded-xl border border-white/10 animate-pulse bg-white/[0.02]" />}
      {data && !data.available && (
        <div data-testid="chart-unavailable" className="h-[220px] rounded-xl border border-dashed border-white/15 grid place-items-center text-center px-6">
          <div><p className="text-sm text-zinc-300">Chart unavailable</p><p className="text-xs text-zinc-500 mt-1">{data.reason}</p>{data.requirements && <p className="text-xs text-zinc-600 mt-1">{data.requirements}</p>}</div>
        </div>
      )}
      {data?.available && <div className="relative"><div ref={el} className="h-[340px] sm:h-[440px] rounded-xl overflow-hidden border border-white/10" />
        <div ref={tip} data-testid="chart-tooltip" className="absolute z-10 hidden w-[220px] rounded-lg border border-white/15 bg-black/90 p-2 text-[11px] text-zinc-200 pointer-events-none" /></div>}
      {data?.available && !conv.locked && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-[11px] text-zinc-400" data-testid="chart-legend">
          <span><span className="inline-block w-5 border-t border-white align-middle mr-1.5" />Entry (exact price line)</span>
          <span><span className="inline-block w-5 border-t border-dashed border-[#ccff00] align-middle mr-1.5" />TP targets</span>
          <span><span className="inline-block w-5 border-t border-dotted border-red-400 align-middle mr-1.5" />Cut loss</span>
          <span>B = entry reference time · TP / SL / X = analyst signals · S↺ T↺ = level updates · TP✓ SL✓ = automatic simulated model exits · t / s = level touched (tracking only). Markers sit on the candle containing the event time.</span>
        </div>
      )}
      {outside.length > 0 && <p className="text-[11px] text-zinc-500 mt-2" data-testid="chart-outside">{outside.length} event(s) fall outside the loaded {tf} data range and aren't drawn. See the timeline.</p>}
    </div>
  );
}
